// multiTabSync.ts - Cross-Tab Session Coordination
import { sessionManager } from './sessionManager'
import { activityTracker } from './activityTracker'
import { sessionWarningService } from './sessionWarningService'

// 🔄 Multi-tab synchronization configuration
const SYNC_CONFIG = {
  // Storage keys for cross-tab communication
  keys: {
    masterTab: 'session_master_tab',
    tabRegistry: 'session_tab_registry',
    coordination: 'session_coordination',
    heartbeat: 'session_tab_heartbeat'
  },
  
  // Timing configuration
  heartbeatInterval: 5000,      // 5 seconds - tab health check
  masterElectionDelay: 1000,   // 1 second delay for master election
  cleanupInterval: 30000,      // 30 seconds - cleanup dead tabs
  
  // Tab coordination
  maxTabs: 10,                 // Maximum tabs to track
  masterTimeout: 15000         // 15 seconds - master tab timeout
} as const

// 🎯 Tab information interface
interface TabInfo {
  id: string
  createdAt: number
  lastHeartbeat: number
  isMaster: boolean
  url: string
  title: string
}

// 📱 Coordination message interface
interface CoordinationMessage {
  type: 'master_election' | 'session_activity' | 'session_warning' | 'session_logout' | 'heartbeat'
  fromTab: string
  timestamp: number
  data?: any
}

/**
 * Multi-Tab Session Sync - Coordinates session management across browser tabs
 * Ensures consistent session state and unified user experience
 */
export class MultiTabSync {
  private isInitialized = false
  private currentTab: TabInfo
  private isMasterTab = false
  private heartbeatTimer?: number
  private cleanupTimer?: number
  private coordinationCallbacks = new Map<string, Set<(message: CoordinationMessage) => void>>()
  
  private readonly DEBUG = process.env.NODE_ENV === 'development'

  constructor() {
    this.currentTab = this.createTabInfo()
    this.log('MultiTabSync initialized', { tabId: this.currentTab.id })
  }

  /**
   * Initialize multi-tab synchronization
   */
  initialize(): void {
    if (this.isInitialized) return

    this.setupStorageListener()
    this.registerCurrentTab()
    this.startHeartbeat()
    this.startCleanupProcess()
    this.setupBeforeUnloadHandler()
    
    // Determine if this should be the master tab
    setTimeout(() => {
      this.electMasterTab()
    }, SYNC_CONFIG.masterElectionDelay)

    this.isInitialized = true
    this.log('MultiTabSync started')
  }

  /**
   * Create tab information object
   */
  private createTabInfo(): TabInfo {
    return {
      id: `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      createdAt: Date.now(),
      lastHeartbeat: Date.now(),
      isMaster: false,
      url: window.location.href,
      title: document.title
    }
  }

  /**
   * Register current tab in the tab registry
   */
  private registerCurrentTab(): void {
    const registry = this.getTabRegistry()
    registry.set(this.currentTab.id, this.currentTab)
    this.saveTabRegistry(registry)
    
    this.log('Tab registered', { count: registry.size })
  }

  /**
   * Get tab registry from localStorage
   */
  private getTabRegistry(): Map<string, TabInfo> {
    try {
      const data = localStorage.getItem(SYNC_CONFIG.keys.tabRegistry)
      if (!data) return new Map()
      
      const parsed = JSON.parse(data)
      return new Map(Object.entries(parsed))
    } catch {
      return new Map()
    }
  }

  /**
   * Save tab registry to localStorage
   */
  private saveTabRegistry(registry: Map<string, TabInfo>): void {
    try {
      const data = JSON.stringify(Object.fromEntries(registry))
      localStorage.setItem(SYNC_CONFIG.keys.tabRegistry, data)
    } catch (error) {
      this.log('Failed to save tab registry', error)
    }
  }

  /**
   * Elect master tab (handles session management for all tabs)
   */
  private electMasterTab(): void {
    const registry = this.getTabRegistry()
    const currentMaster = this.getCurrentMaster(registry)
    
    // If no master or master is dead, become master
    if (!currentMaster || this.isTabDead(currentMaster)) {
      this.becomeMasterTab()
    } else if (currentMaster.id === this.currentTab.id) {
      this.isMasterTab = true
      this.log('Confirmed as master tab')
    }
  }

  /**
   * Get current master tab
   */
  private getCurrentMaster(registry: Map<string, TabInfo>): TabInfo | null {
    for (const tab of registry.values()) {
      if (tab.isMaster && !this.isTabDead(tab)) {
        return tab
      }
    }
    return null
  }

  /**
   * Check if tab is dead (no recent heartbeat)
   */
  private isTabDead(tab: TabInfo): boolean {
    return Date.now() - tab.lastHeartbeat > SYNC_CONFIG.masterTimeout
  }

  /**
   * Become the master tab
   */
  private becomeMasterTab(): void {
    this.isMasterTab = true
    this.currentTab.isMaster = true
    
    // Clear any existing master
    const registry = this.getTabRegistry()
    for (const tab of registry.values()) {
      if (tab.id !== this.currentTab.id) {
        tab.isMaster = false
      }
    }
    
    // Update registry
    registry.set(this.currentTab.id, this.currentTab)
    this.saveTabRegistry(registry)
    
    // Set master tab marker
    localStorage.setItem(SYNC_CONFIG.keys.masterTab, this.currentTab.id)
    
    // Initialize session management (only master manages session)
    this.initializeMasterTabFeatures()
    
    this.log('Became master tab')
    this.broadcastMessage({
      type: 'master_election',
      fromTab: this.currentTab.id,
      timestamp: Date.now(),
      data: { newMaster: this.currentTab.id }
    })
  }

  /**
   * Initialize features that only the master tab should handle
   */
  private initializeMasterTabFeatures(): void {
    // Only master tab initializes session management
    sessionManager.initialize()
    activityTracker.start()
    sessionWarningService.initialize()
    
    this.log('Master tab features initialized')
  }

  /**
   * Start heartbeat to indicate tab is alive
   */
  private startHeartbeat(): void {
    this.heartbeatTimer = window.setInterval(() => {
      this.sendHeartbeat()
    }, SYNC_CONFIG.heartbeatInterval)
  }

  /**
   * Send heartbeat signal
   */
  private sendHeartbeat(): void {
    this.currentTab.lastHeartbeat = Date.now()
    
    const registry = this.getTabRegistry()
    registry.set(this.currentTab.id, this.currentTab)
    this.saveTabRegistry(registry)
    
    // If we're master, ensure session manager is active
    if (this.isMasterTab) {
      // Update master timestamp
      localStorage.setItem(SYNC_CONFIG.keys.heartbeat, Date.now().toString())
    }
  }

  /**
   * Start cleanup process for dead tabs
   */
  private startCleanupProcess(): void {
    this.cleanupTimer = window.setInterval(() => {
      this.cleanupDeadTabs()
    }, SYNC_CONFIG.cleanupInterval)
  }

  /**
   * Clean up dead tabs from registry
   */
  private cleanupDeadTabs(): void {
    const registry = this.getTabRegistry()
    const initialSize = registry.size
    let masterRemoved = false
    
    for (const [tabId, tab] of registry.entries()) {
      if (this.isTabDead(tab)) {
        registry.delete(tabId)
        if (tab.isMaster) {
          masterRemoved = true
        }
      }
    }
    
    // Limit registry size
    if (registry.size > SYNC_CONFIG.maxTabs) {
      const sortedTabs = Array.from(registry.entries())
        .sort(([, a], [, b]) => a.createdAt - b.createdAt)
      
      // Keep most recent tabs
      const toKeep = sortedTabs.slice(-SYNC_CONFIG.maxTabs)
      registry.clear()
      toKeep.forEach(([id, tab]) => registry.set(id, tab))
    }
    
    if (registry.size !== initialSize) {
      this.saveTabRegistry(registry)
      this.log('Cleaned up dead tabs', { removed: initialSize - registry.size })
    }
    
    // If master was removed, elect new master
    if (masterRemoved && !this.isMasterTab) {
      this.electMasterTab()
    }
  }

  /**
   * Setup storage event listener for cross-tab communication
   */
  private setupStorageListener(): void {
    window.addEventListener('storage', (event) => {
      if (event.key === SYNC_CONFIG.keys.coordination && event.newValue) {
        try {
          const message: CoordinationMessage = JSON.parse(event.newValue)
          
          // Don't process our own messages
          if (message.fromTab === this.currentTab.id) return
          
          this.handleCoordinationMessage(message)
        } catch (error) {
          this.log('Error parsing coordination message', error)
        }
      }
    })
  }

  /**
   * Enhanced handleCoordinationMessage to handle sync requests
   */
  private handleCoordinationMessage(message: CoordinationMessage): void {
    this.log('Received coordination message', { type: message.type, from: message.fromTab.slice(-4) })
    
    switch (message.type) {
      case 'master_election':
        this.handleMasterElection(message)
        break
      case 'session_activity':
        this.handleSessionActivity(message)
        break
      case 'session_warning':
        this.handleSessionWarning(message)
        break
      case 'session_logout':
        this.handleSessionLogout(message)
        break
      case 'session_sync_request':
        this.handleSessionSyncRequest(message)
        break
      case 'session_sync_response':
        this.handleSessionSyncResponse(message)
        break
    }
    
    // Notify registered callbacks
    const callbacks = this.coordinationCallbacks.get(message.type)
    if (callbacks) {
      callbacks.forEach(callback => {
        try {
          callback(message)
        } catch (error) {
          this.log('Error in coordination callback', error)
        }
      })
    }
  }

  /**
   * Handle session sync requests from new tabs
   */
  private handleSessionSyncRequest(message: CoordinationMessage): void {
    // Only master tab responds to sync requests
    if (!this.isMasterTab) return
    
    this.log('Received session sync request from new tab', { fromTab: message.fromTab.slice(-4) })
    
    // Send session data to requesting tab
    this.broadcastMessage({
      type: 'session_sync_response',
      fromTab: this.currentTab.id,
      timestamp: Date.now(),
      data: {
        requestingTab: message.fromTab,
        sessionData: {
          hasValidSession: true,
          timestamp: Date.now()
        }
      }
    })
  }

  /**
   * Handle session sync responses
   */
  private handleSessionSyncResponse(message: CoordinationMessage): void {
    // Only process if this response is for us
    if (message.data?.requestingTab !== this.currentTab.id) return
    
    this.log('Received session sync response from master tab')
    
    // Trigger auth state refresh in the auth context
    if (typeof window !== 'undefined' && (window as any).triggerAuthRefresh) {
      (window as any).triggerAuthRefresh()
    }
  }
  /**
   * Handle master election messages
   */
  private handleMasterElection(message: CoordinationMessage): void {
    if (this.isMasterTab && message.data?.newMaster !== this.currentTab.id) {
      // Another tab became master, step down
      this.isMasterTab = false
      this.currentTab.isMaster = false
      this.log('Stepped down as master tab')
      
      // Stop master-only features
      activityTracker.stop()
      sessionWarningService.cleanup()
    }
  }

  /**
   * Handle session activity from other tabs
   */
  private handleSessionActivity(message: CoordinationMessage): void {
    // Only master tab processes activity
    if (this.isMasterTab) {
      sessionManager.recordActivity()
    }
  }

  /**
   * Handle session warning coordination
   */
  private handleSessionWarning(message: CoordinationMessage): void {
    // All tabs should show warnings, but only if they're not the master
    // (master tab handles its own warnings)
    if (!this.isMasterTab && message.data) {
      // Mirror warning in this tab
      this.log('Mirroring session warning from master tab', message.data)
    }
  }

  /**
   * Handle session logout coordination
   */
  private handleSessionLogout(message: CoordinationMessage): void {
    // All tabs should handle logout
    this.cleanup()
    
    // Navigate to logout page
    window.location.href = '/welcome?fromLogout=true'
  }

  /**
   * Broadcast message to other tabs
   */
  private broadcastMessage(message: CoordinationMessage): void {
    try {
      const data = JSON.stringify(message)
      localStorage.setItem(SYNC_CONFIG.keys.coordination, data)
      
      // Remove after short delay to trigger storage event
      setTimeout(() => {
        localStorage.removeItem(SYNC_CONFIG.keys.coordination)
      }, 100)
    } catch (error) {
      this.log('Failed to broadcast message', error)
    }
  }

  /**
   * Setup beforeunload handler to clean up on tab close
   */
  private setupBeforeUnloadHandler(): void {
    window.addEventListener('beforeunload', () => {
      this.unregisterCurrentTab()
    })
  }

  /**
   * Unregister current tab on close
   */
  private unregisterCurrentTab(): void {
    const registry = this.getTabRegistry()
    registry.delete(this.currentTab.id)
    this.saveTabRegistry(registry)
    
    // If we were master, clear master marker
    if (this.isMasterTab) {
      localStorage.removeItem(SYNC_CONFIG.keys.masterTab)
    }
  }

  /**
   * Register callback for coordination messages
   */
  onCoordinationMessage(type: CoordinationMessage['type'], callback: (message: CoordinationMessage) => void): () => void {
    if (!this.coordinationCallbacks.has(type)) {
      this.coordinationCallbacks.set(type, new Set())
    }
    
    this.coordinationCallbacks.get(type)!.add(callback)
    
    return () => {
      this.coordinationCallbacks.get(type)?.delete(callback)
    }
  }

  /**
   * Broadcast activity to other tabs
   */
  broadcastActivity(): void {
    this.broadcastMessage({
      type: 'session_activity',
      fromTab: this.currentTab.id,
      timestamp: Date.now()
    })
  }

  /**
   * Broadcast logout to other tabs
   */
  broadcastLogout(): void {
    this.broadcastMessage({
      type: 'session_logout',
      fromTab: this.currentTab.id,
      timestamp: Date.now()
    })
  }

  /**
   * Get current tab information
   */
  getTabInfo(): {
    currentTab: TabInfo
    isMaster: boolean
    totalTabs: number
    registry: TabInfo[]
  } {
    const registry = this.getTabRegistry()
    
    return {
      currentTab: this.currentTab,
      isMaster: this.isMasterTab,
      totalTabs: registry.size,
      registry: Array.from(registry.values())
    }
  }

  /**
   * Cleanup on logout or tab close
   */
  cleanup(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer)
    }
    
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer)
    }
    
    // Clean up session features if master
    if (this.isMasterTab) {
      activityTracker.stop()
      sessionWarningService.cleanup()
      sessionManager.cleanup()
    }
    
    this.unregisterCurrentTab()
    this.coordinationCallbacks.clear()
    this.isInitialized = false
    
    this.log('MultiTabSync cleaned up')
  }

  /**
   * Debug logging
   */
  private log(message: string, data?: any): void {
    if (this.DEBUG) {
      console.log(`[MultiTabSync-${this.currentTab.id.slice(-4)}] ${message}`, data || '')
    }
  }

  /**
   * Force sync session data from master tab to this tab
   * Called when a new tab detects cross-tab session but has no local session
   */
  syncSessionFromMasterTab(): void {
    const registry = this.getTabRegistry()
    const masterTab = this.getCurrentMaster(registry)
    
    if (masterTab && masterTab.id !== this.currentTab.id) {
      this.log('Syncing session data from master tab', { masterId: masterTab.id.slice(-4) })
      
      // Broadcast a request for session sync
      this.broadcastMessage({
        type: 'session_sync_request',
        fromTab: this.currentTab.id,
        timestamp: Date.now()
      })
    }
  }
}

// Export singleton instance
export const multiTabSync = new MultiTabSync()